import { useEffect, useMemo, useRef, useState } from "react";

const clamp = (value, min = 0, max = 1) => Math.min(Math.max(value, min), max);

export const getTextWipeLineProgress = (progress, lineIndex, lineCount) => {
  const count = Math.max(1, lineCount);
  return clamp(clamp(progress) * count - lineIndex);
};

export const buildTextWipeMask = (progress, lineCount, isExit = false) => {
  const count = Math.max(1, lineCount);
  const gradients = [];
  const sizes = [];
  const positions = [];

  for (let index = 0; index < count; index += 1) {
    const lineProgress = getTextWipeLineProgress(progress, index, count) * 100;
    const stop = isExit ? lineProgress : 100 - lineProgress;
    gradients.push(
      isExit
        ? `linear-gradient(to right, transparent ${stop}%, #000 ${stop}%)`
        : `linear-gradient(to right, #000 ${lineProgress}%, transparent ${lineProgress}%)`
    );
    sizes.push(`100% ${100 / count}%`);
    positions.push(`0 ${index * 100 / Math.max(1, count - 1)}%`);
  }

  return {
    maskImage: gradients.join(", "),
    WebkitMaskImage: gradients.join(", "),
    maskSize: sizes.join(", "),
    WebkitMaskSize: sizes.join(", "),
    maskPosition: positions.join(", "),
    WebkitMaskPosition: positions.join(", "),
    maskRepeat: "no-repeat",
    WebkitMaskRepeat: "no-repeat",
  };
};

/** Measures the browser's actual wrapped text, then wipes each visual line in order. */
const TextWipeLayer = ({ children, className, style, progress, isExit = false }) => {
  const textRef = useRef(null);
  const [lineCount, setLineCount] = useState(1);

  useEffect(() => {
    const textElement = textRef.current;
    if (!textElement) return undefined;

    const measure = () => {
      const computed = window.getComputedStyle(textElement);
      const lineHeight = Number.parseFloat(computed.lineHeight);
      const height = textElement.getBoundingClientRect().height;
      if (lineHeight > 0 && height > 0) {
        setLineCount(Math.max(1, Math.round(height / lineHeight)));
      }
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(textElement);
    document.fonts?.ready.then(measure);
    return () => observer.disconnect();
  }, [children, style?.fontFamily, style?.fontSize, style?.fontWeight, style?.fontStyle, style?.lineHeight]);

  const maskStyle = useMemo(
    () => progress == null ? {} : buildTextWipeMask(progress, lineCount, isExit),
    [progress, lineCount, isExit]
  );

  return (
    <div className={className} style={{ ...style, ...maskStyle }}>
      <span ref={textRef} style={{ maxWidth: "100%", maxHeight: "100%", overflow: "hidden" }}>
        {children}
      </span>
    </div>
  );
};

export default TextWipeLayer;
